import { useEffect } from "react";
import toast from "react-hot-toast";
import { useDebouncedCallback } from "use-debounce";
import { supabase } from "../../supabase";
import saveColumnPreferences from "../../utils/saveColumnPreferences";
import { getUrlParams } from "../../utils/urlParams";
import enrichedData from "./enrichedData";
import fullyNormalized from "./fullyNormalized";
import { filterInfluencersByCheckStatus } from "./checkStatusFilter";
import {
  categorizeFilters,
  applyRelatedTableFilters,
  applyRegularFilters,
} from "./helpers";
import { getTableForColumn, getPresetColumns } from "../../components/MainContent/hooks/useTableColumnsConfig";

export const RELATED_TABLES = [
  "influencer_free_checks",
  "influencer_paid_checks", 
  "internal_reviews",
];

export default function useGetData({
  setAvailableUsers,
  setRows,
  setLoadingData,
  setIsInitialLoad,
  setCurrentColumnPreset,
  setVisibleColumns,
  setFilters,
  setFiltersLogic,
  rowsPerPage,
  page,
  setRowsCount,
  rows,
  searchQuery,
  setSearchQuery,
  sortConfig,
  setSortConfig,
  showOnlyApproved,
  checkStatusFilter,
  filters,
  filtersLogic,
  setPage,
  setRowsPerPage,
  setAllDataForExport,
}) {
  useEffect(() => {
    // USERS
    const fetchInitialData = async () => {
      const { data, error } = await supabase.from("user").select();
      if (error) {
        toast.error("Failed to load users. Please refresh the page.");
        return;
      }
      setAvailableUsers(data);
    };
    fetchInitialData();

    // QUERY PARAMS
    const {
      page: urlPage,
      rowsPerPage: urlRowsPerPage,
      searchQuery: restoredSearchQuery,
      filters: restoredFilters,
      logic: restoredLogic,
      sortConfig: restoredSortConfig,
    } = getUrlParams();

    setPage(urlPage);
    setRowsPerPage(urlRowsPerPage);
    setSearchQuery(restoredSearchQuery);
    // Filter out empty filters when initializing from URL
    const validFilters = restoredFilters.filter(filter => filter.value && filter.value.trim() !== "");
    setFilters(validFilters);
    setFiltersLogic(restoredLogic);
    setSortConfig(restoredSortConfig);
  }, []);

  const fetchData = async ({isExportMode = false}) => {
    setLoadingData(true);
    const startIndex = isExportMode ? 0 : page * rowsPerPage;
    const endIndex = isExportMode ? undefined : startIndex + rowsPerPage - 1;
    let data = [];
    let count = 0;

    try {
      let query = supabase
        .from("influencers")
        .select(`
          id,
          link,
          username,
          platform,
          status,
          refusal_reason,
          created_at,
          assigned_member,
          influencer_free_checks(*),
          influencer_paid_checks(*),
          internal_reviews(*)
        `, 
        { count: "exact" }
      );

      // SEARCH
      const searchTerm = searchQuery?.trim();
      if (searchTerm) {
        query = query.or(
          `username.ilike.%${searchTerm}%,platform.ilike.%${searchTerm}%`
        );
      }

      // FILTER STATUS
      if (showOnlyApproved) {
        query = query.in("status", ["Approved Paid", "Approved Barter"]);
      }

      // FILTER BY CHECK STATUS
      if (checkStatusFilter) {
        const cleanIds = await filterInfluencersByCheckStatus(
          supabase,
          checkStatusFilter
        );
        const pageIds = cleanIds?.slice(startIndex, endIndex);
        if (pageIds?.length > 0) {
          query = query.in("id", pageIds);
        }
        count = cleanIds?.length || 0;
      }

      // FILTERS
      if (filters.length > 0) {
        const { relatedTableFilters, regularFilters } = categorizeFilters(filters);
        const cleanIds = await applyRelatedTableFilters(supabase, relatedTableFilters);
        if (cleanIds?.length > 0) {
          query = query.in("id", cleanIds);
          count = cleanIds.length;
        }
        query = applyRegularFilters(query, regularFilters, filtersLogic);
      }

      // SORT
      if (sortConfig.direction) {
        const tableName = getTableForColumn({ columnKey: sortConfig.key });
        const order =
          tableName === "influencers"
            ? sortConfig.key
            : `${tableName}(${sortConfig.key})`;
        query = query.order(order, {
          ascending: sortConfig.direction === "asc",
        });
      }

      // DATA FETCH
      if (checkStatusFilter) {
        const influencersResult = await query;
        data = influencersResult.data;
      } else if (isExportMode) {
        const batchSize = 1000;
        let from = 0;
        let hasMore = true;
        let allData = [];

        while (hasMore) {
          const batchResult = await query.range(from, from + batchSize - 1);

          if (batchResult.error) {
            throw batchResult.error;
          }

          if (batchResult.data && batchResult.data.length > 0) {
            allData = [...allData, ...batchResult.data];
            from += batchSize;

            if (batchResult.data.length < batchSize) {
              hasMore = false;
            }
          } else {
            hasMore = false;
          }
        }

        data = allData;
        count = allData.length;
      } else {
        const influencersResult = await query.range(startIndex, endIndex);
        data = influencersResult.data;
        count = influencersResult.count;
      }

    } catch (e) {
      console.log(e);
      toast.error("Failed to load data. Please retry.");
      setLoadingData(false);
      return []; // Return empty array on error for export mode
    }

    const enrichedDataResult = enrichedData(data || []);
    const fullyNormalizedResult = fullyNormalized(enrichedDataResult);
    setLoadingData(false);
    setIsInitialLoad(false);
    if (isExportMode) {
      setAllDataForExport(fullyNormalizedResult || []);
      return fullyNormalizedResult || [];
    } else {
      setRows(fullyNormalizedResult || []);
      setRowsCount(count || 0);
    }
    // GENERATE COLUMNS
    if (isExportMode) {
      return fullyNormalizedResult || [];
    }
  };

  const fetchDataTable = useDebouncedCallback(
    async () => {
      return await fetchData({ isExportMode: false });
    },
    rows.length > 0 ? 100 : 0
  );

  const fetchDataForExport = async () => {
    return await fetchData({ isExportMode: true });
  };

  useEffect(() => {
    fetchDataTable();
  }, [
    page,
    rowsPerPage,
    searchQuery,
    sortConfig,
    showOnlyApproved,
    checkStatusFilter,
    filters,
    filtersLogic,
  ]);

  // Load column preferences from localStorage (client-side only)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const loadColumnPreferences = () => {
      try {
        const saved = localStorage.getItem("columnPreferences");
        if (saved) {
          const { preset, customColumns } = JSON.parse(saved);
          const presetKey = preset?.split(" ")?.at(0) || preset;
          setCurrentColumnPreset(presetKey?.toLowerCase() || "default");
          return customColumns;
        }
      } catch (err) {
        console.error("Failed to load column preferences:", err);
      }
    };

    const defaultVisibleColumns = getPresetColumns({ presetKey: "default" });
    const savedColumns = loadColumnPreferences();
    if (savedColumns) {
      setVisibleColumns(savedColumns);
    } else {
      setVisibleColumns(defaultVisibleColumns);
      saveColumnPreferences({ presetKey: "default", columns: defaultVisibleColumns });
    }
  }, []);

  return { fetchDataTable, fetchDataForExport };
}
